import { FC, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Modal, Svg } from '@hopskipdrive/react-components';
import { ButtonLink } from '@rideiq/shared/components';
import { Button } from '@rideiq/shared/components/Button';

import { analyticsNewOrganizerCreated } from '../../../api/services/Analytics';
import { createOrganizer } from '../../../api/services/Organizers';
import { useFlags } from '../../../contexts/LDContext';
import { useToastStore } from '../../../shared/stores/toastStore';
import { UserForm } from '../../components/UserForm';
import { IUserFormType } from '../../components/UserForm/types';
import { userInfoSchema } from '../../components/UserForm/validationSchema';
import { useJobTitles } from '../../hooks/useJobTitles';
import { useRoles } from '../../hooks/useRoles';
import { setServerFormErrors } from '../../utils/setServerFormErrors';

const defaultValues: IUserFormType = {
  firstName: '',
  lastName: '',
  email: '',
  mobilePhoneNumber: '',
  role: null,
};

export const NewOrganizer: FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { roleOptions, roleDescriptionMap, isFetching } = useRoles();
  const { addToast } = useToastStore();
  const { enableDailyQueueAssignedLocations, jobTitleDropdownEnabled } = useFlags();
  const { jobTitleOptions, isFetching: jobTitlesIsFetching } = useJobTitles(jobTitleDropdownEnabled);

  const methods = useForm<IUserFormType>({
    defaultValues: defaultValues,
    resolver: yupResolver(userInfoSchema(enableDailyQueueAssignedLocations)),
  });

  const { setError } = methods;

  const handleSubmit = async (data) => {
    setIsSubmitting(true);
    const { role, defaultAccountLocation, accountLocations, jobTitle, ...rest } = data;
    const payload = {
      ...rest,
      ...(defaultAccountLocation && { defaultAccountLocationId: defaultAccountLocation.id }),
      ...(accountLocations?.length && { accountLocationIds: accountLocations.map((loc) => loc.id) }),
      ...(jobTitle && { jobTitle: jobTitle }),
      roles: [role],
    };
    const response = await createOrganizer(payload);

    setIsSubmitting(false);

    if (response.type === 'success') {
      analyticsNewOrganizerCreated({ role, organizerSlug: response.data.organizer.uuid });
      navigate(`/settings/organizers/${response.data.organizer.uuid}`);
    } else {
      const validationErrors = response.data?.errors;

      validationErrors && setServerFormErrors({ setError, validationErrors, addToast });
    }
  };

  return (
    <Modal open={true} maxWidth="xl" data-testid="edit-organizer-info">
      <Modal.Title>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          Add User
          <Link to=".." data-testid="closeButton" aria-label="Close">
            <Svg height="20px" icon="close-outlined" />
          </Link>
        </Box>
      </Modal.Title>
      <Modal.Content>
        <FormProvider {...methods}>
          <UserForm
            handleSubmit={handleSubmit}
            rolesInfo={{ roleOptions, roleDescriptionMap, isFetching }}
            jobTitlesInfo={{ jobTitleOptions, isFetching: jobTitlesIsFetching }}
          />
        </FormProvider>
      </Modal.Content>
      <Modal.Actions>
        <ButtonLink color="tertiary" to="..">
          Cancel
        </ButtonLink>
        <Button type="submit" color="primary" form="user-info-form" loading={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Save'}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
